import React, { useEffect, useState, useMemo } from 'react';
import { FiPlus, FiRefreshCw, FiUsers } from 'react-icons/fi';
import { StudentDirectoryTable } from '../tables/PageTables.jsx';
import { getLibraries, getSeatsData, getStudentsSummary, updateStudent, getPaymentPlans } from '../services/api.js';
import { prefetchStudentFormData } from './StudentEditorModal.jsx';
import StudentDetailsModal from '../components/StudentDetailsModal.jsx';
import StudentFilters from './StudentFilters.jsx';
import { useUrlFilters, intParam } from '../hooks/useUrlFilters.js';
import '../styles/pages/students.css';
import '../styles/pages/StudentFilters.css';

const PLAN_LABELS = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  half_yearly: 'Half Yearly',
  halfyearly: 'Half Yearly',
  yearly: 'Yearly',
  annual: 'Yearly',
};

const formatPlanLabel = value => {
  const key = String(value || '').toLowerCase().replace(/[\s-]+/g, '_');
  return PLAN_LABELS[key] || (key ? key.replaceAll('_', ' ').replace(/\b\w/g, c => c.toUpperCase()) : '—');
};

const formatDateLabel = value => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const SKELETON_ROWS = 7;

function StudentsSkeleton() {
  return (
    <div className="table-scroll student-skel-scroll" aria-label="Loading student records" aria-hidden="true">
      <table className="student-table student-skel-table">
        <colgroup>
          <col className="student-col" />
          <col className="contact-col" />
          <col className="allocation-col" />
          <col className="plan-col" />
          <col className="payment-col" />
          <col className="status-col" />
          <col className="action-col" />
        </colgroup>
        <thead>
          <tr>
            {['Student', 'Contact', 'Allocation', 'Plan', 'Payment', 'Status', 'Actions'].map(label => (
              <th key={label}>{label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: SKELETON_ROWS }).map((_, row) => (
            <tr key={row}>
              <td className="student-primary-cell">
                <div className="student-cell students-skel-student">
                  <span className="dash-skel-icon small" />
                  <div className="students-skel-copy">
                    <span className="dash-skel-line wide" />
                    <span className="dash-skel-line short" />
                  </div>
                </div>
              </td>
              <td>
                <div className="students-skel-copy">
                  <span className="dash-skel-line medium" />
                  <span className="dash-skel-line short" />
                </div>
              </td>
              <td>
                <div className="students-skel-copy">
                  <span className="dash-skel-chip" />
                  <span className="dash-skel-line medium" />
                </div>
              </td>
              <td>
                <div className="students-skel-copy">
                  <span className="dash-skel-line medium" />
                  <span className="dash-skel-line short" />
                </div>
              </td>
              <td>
                <div className="students-skel-copy">
                  <span className="dash-skel-line medium" />
                  <span className="dash-skel-line short" />
                </div>
              </td>
              <td className="status-cell">
                <span className="dash-skel-pill students-skel-status" />
              </td>
              <td className="action-cell">
                <div className="students-skel-actions">
                  <span className="dash-skel-icon small" />
                  <span className="dash-skel-icon small" />
                  <span className="dash-skel-icon small" />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function StudentPage({ query, setQuery, filter, setFilter, openStudent, onEdit, onDelete, onPay, onNotify, toast }) {
  const [students, setStudents] = useState([]);
  const [libraries, setLibraries] = useState([]);
  const [seats, setSeats] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Extracted filter states from first file
  const [filterRoom, setFilterRoom] = useState("all");
  const [filterSubscription, setFilterSubscription] = useState("all");
  const [filterPlanType, setFilterPlanType] = useState("all");
  const [filterSeatCategory, setFilterSeatCategory] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [detailsStudent, setDetailsStudent] = useState(null);
  const [statusSavingId, setStatusSavingId] = useState(null);

  useUrlFilters([
    { key: 'q', value: query, setValue: setQuery, defaultValue: '' },
    { key: 'filter', value: filter, setValue: setFilter, defaultValue: 'All' },
    { key: 'status', value: filterStatus, setValue: setFilterStatus, defaultValue: 'all' },
    { key: 'room', value: filterRoom, setValue: setFilterRoom, defaultValue: 'all' },
    { key: 'subscription', value: filterSubscription, setValue: setFilterSubscription, defaultValue: 'all' },
    { key: 'plan', value: filterPlanType, setValue: setFilterPlanType, defaultValue: 'all' },
    { key: 'seat', value: filterSeatCategory, setValue: setFilterSeatCategory, defaultValue: 'all' },
    { key: 'page', value: currentPage, setValue: setCurrentPage, defaultValue: 1, parse: intParam(1, 100000) },
    { key: 'perPage', value: itemsPerPage, setValue: setItemsPerPage, defaultValue: 10, parse: intParam(1, 100) },
  ]);

  const loadData = async (forceRefresh = false, onlyStudents = false) => {
    setRefreshing(true);
    if (onlyStudents) {
      try {
        const studentData = await getStudentsSummary(null, forceRefresh);
        const studentList = Array.isArray(studentData)
          ? studentData
          : studentData?.students || studentData?.data || [];
        setStudents(studentList);
      } catch (e) {
        console.error("Students refresh failed", e);
      } finally {
        setRefreshing(false);
      }
      return;
    }

    const seatsPromise = getSeatsData(forceRefresh).catch(error => {
      console.error('Students seat data failed', error);
      return { seats: [], rooms: [] };
    });
    const librariesPromise = getLibraries().catch(error => {
      console.error('Students library data failed', error);
      return [];
    });

    try {
      const studentData = await getStudentsSummary(null, forceRefresh);
      const studentList = Array.isArray(studentData)
        ? studentData
        : studentData?.students || studentData?.data || [];
      setStudents(studentList);
      setLoading(false);
      setRefreshing(false);

      const [seatData, libraryData] = await Promise.all([seatsPromise, librariesPromise]);
      setSeats(seatData.seats || []);
      setRooms(seatData.rooms || []);
      setLibraries(libraryData || []);
    } catch (e) {
      console.error("Students load failed", e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { loadData(false); prefetchStudentFormData(); }, []);

  // Listen for data update events from other components
  useEffect(() => {
    const handleStudentUpdate = () => {
      console.log('🔄 Students: Student data updated, refreshing...');
      loadData(true);
    };

    const handlePaymentUpdate = () => {
      console.log('🔄 Students: Payment data updated, refreshing...');
      loadData(true);
    };

    window.addEventListener('studentDataUpdated', handleStudentUpdate);
    window.addEventListener('paymentDataUpdated', handlePaymentUpdate);

    return () => {
      window.removeEventListener('studentDataUpdated', handleStudentUpdate);
      window.removeEventListener('paymentDataUpdated', handlePaymentUpdate);
    };
  }, []);

  const handleStatusChange = async (student, nextStatus) => {
    const studentId = student?.studentId || student?.student_id || student?.studentid || student?.id || student?._id;
    const currentStatus = String(student?.status_normalized || student?.status || 'active').toLowerCase();
    const next = String(nextStatus || '').toLowerCase();
    if (!studentId || !next || next === currentStatus || statusSavingId) return;

    const applyStatus = value => {
      setStudents(prev => prev.map(item => {
        const id = item.student_id || item.studentId || item.studentid || item.id || item._id;
        return String(id) === String(studentId) ? { ...item, status: value, status_normalized: value } : item;
      }));
      setDetailsStudent(prev => {
        if (!prev) return prev;
        const id = prev.studentId || prev.student_id || prev.studentid || prev.id || prev._id;
        return String(id) === String(studentId) ? { ...prev, status: value, status_normalized: value } : prev;
      });
    };

    setStatusSavingId(String(studentId));
    applyStatus(next);
    try {
      await updateStudent(studentId, { status: next });
      toast?.(`Student status updated to ${next}.`, 'success');
    } catch (error) {
      console.error('Student status update failed', error);
      applyStatus(currentStatus);
      toast?.(error?.response?.data?.error || error?.message || 'Could not update student status.', 'error');
    } finally {
      setStatusSavingId(null);
    }
  };

  const libMap = useMemo(() => {
    const m = new Map();
    libraries.forEach(l => {
      const name = l.library_name || l.name;
      [l.id, l.library_id, l._id].filter(id => id != null).forEach(id => {
        m.set(id, name);
        m.set(String(id), name);
      });
    });
    return m;
  }, [libraries]);

  const seatMap = useMemo(() => {
    const m = new Map();
    seats.forEach(s => {
      const key = s.id || s.seat_id || s.seatid;
      if (key != null) m.set(String(key), s);
    });
    return m;
  }, [seats]);

  const roomMap = useMemo(() => {
    const m = new Map();
    rooms.forEach(room => {
      [room.id, room.room_id, room.roomid].filter(id => id != null).forEach(id => {
        m.set(String(id), room);
      });
    });
    return m;
  }, [rooms]);

  const enrichedStudents = useMemo(() => {
    return students.map(s => {
      const studentId = s.student_id || s.studentId || s.studentid || s.id || s._id;
      const libName = libMap.get(s.library_id) || libMap.get(String(s.library_id)) || s.library_name || 'Unknown';
      const seatId = s.seatid || s.seat_id;
      const seat = seatId != null ? seatMap.get(String(seatId)) : null;
      const roomId = s.room_id || seat?.room_id || seat?.roomid;
      const room = roomId != null ? roomMap.get(String(roomId)) : null;
      const joinedDate = formatDateLabel(s.admission_date || s.started_at || s.created_at || s.joined_date);
      const endDate = formatDateLabel(s.subscription_end || s.end_date);
      const due = Number(s.total_amount_due ?? s.amount_due ?? s.subscription_amount_due ?? s.due ?? 0);
      const paid = Number(s.total_paid_amount ?? s.paid_amount ?? s.subscription_paid_amount ?? 0);
      const rawDays = s.days_remaining ?? s.daysremaining;
      let daysRemaining = rawDays != null && rawDays !== '' ? Number(rawDays) : null;
      if (!Number.isFinite(daysRemaining)) {
        const end = s.subscription_end || s.end_date;
        const date = end ? new Date(end) : null;
        daysRemaining = date && !Number.isNaN(date.getTime()) ? Math.ceil((date.getTime() - Date.now()) / 86400000) : null;
      }
      return {
        ...s,
        id: studentId,
        studentId,
        library_name_display: libName,
        seat_number_display: s.seatnumber || s.seat_number || seat?.seat_number || seat?.seatnumber || (s.seat_no ? String(s.seat_no) : '—'),
        room_name_display: s.room_name || seat?.room_name || seat?.roomname || room?.room_name || room?.roomname || '—',
        seat_category_display: s.seatcategory || s.seat_category || seat?.category || seat?.seat_category || '',
        plan_type_display: s.plan_type || 'general',
        subscription_display: formatPlanLabel(s.subscription_type || s.subscription),
        joined_date: joinedDate,
        end_date: endDate,
        days_remaining: daysRemaining,
        amount_due: due,
        paid_amount: paid,
        payment_status: s.payment_status || (due > 0 ? 'outstanding' : 'paid'),
        status_normalized: (s.status || 'active').toLowerCase(),
      };
    });
  }, [students, libMap, seatMap, roomMap]);

  // Combined filter logic from first file
  const filtered = useMemo(() => {
    let result = [...enrichedStudents];
    const search = (query || '').toLowerCase();
    if (search) {
      result = result.filter(s =>
        (s.name || '').toLowerCase().includes(search) ||
        (s.email || '').toLowerCase().includes(search) ||
        (s.library_name_display || '').toLowerCase().includes(search) ||
        (s.seat_number_display || '').toLowerCase().includes(search) ||
        (s.phone || '').includes(search)
      );
    }
    if (filter !== 'All') {
      result = result.filter(s => s.status_normalized === filter.toLowerCase());
    }
    if (filterStatus !== 'all') {
      result = result.filter(s => s.status_normalized === filterStatus);
    }
    if (filterRoom !== 'all') {
      result = result.filter(s => String(s.room_id || s.roomid) === String(filterRoom) || (s.room_name_display || '').toLowerCase() === String(filterRoom).toLowerCase());
    }
    if (filterSubscription !== 'all') {
      result = result.filter(s => (s.subscription_display || '').toLowerCase().includes(filterSubscription.toLowerCase()));
    }
    if (filterPlanType !== 'all') {
      result = result.filter(s => (s.plan_type_display || 'general') === filterPlanType);
    }
    if (filterSeatCategory !== 'all') {
      result = result.filter(s => (s.seat_category_display || '').toLowerCase() === filterSeatCategory);
    }
    return result;
  }, [enrichedStudents, query, filter, filterStatus, filterRoom, filterSubscription, filterPlanType, filterSeatCategory]);

  // Pagination
  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const currentStudents = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const clearFilters = () => {
    setQuery('');
    setFilter('All');
    setFilterStatus('all');
    setFilterRoom('all');
    setFilterSubscription('all');
    setFilterPlanType('all');
    setFilterSeatCategory('all');
    setCurrentPage(1);
  };

  return (
    <>
    <header className="dash-header students-page-header">
        <div className="dash-header-bar" style={{ marginBottom: '16px' }}>
          <div className="students-header-left">
            <div className="students-header-icon">
              <FiUsers aria-hidden="true" />
            </div>
            <div className="dash-header-info students-header-info">
              <div className="students-heading-copy">
                <h1 className="dash-header-title">Students</h1>
                <p className="dash-header-subtitle">Manage your students</p>
              </div>
              <div className="dash-header-actions">
                <button type="button" className="dash-action-btn" onClick={() => loadData(true, true)} disabled={refreshing} aria-label="Refresh students">
                <span className={`dash-action-icon ${refreshing ? 'is-loading' : ''}`}><FiRefreshCw aria-hidden="true" /></span>
                <span>{refreshing ? 'Refreshing' : 'Refresh'}</span>
                </button>
                <button type="button" className="dash-action-btn primary" onClick={() => { getSeatsData(true).catch(()=>{}); getLibraries().catch(()=>{}); getPaymentPlans().catch(()=>{}); openStudent(); }}>
                  <FiPlus aria-hidden="true" />
                  <span>Add Student</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <StudentFilters
        searchTerm={query}
        setSearchTerm={setQuery}
        filterStatus={filterStatus}
        setFilterStatus={setFilterStatus}
        filterRoom={filterRoom}
        setFilterRoom={setFilterRoom}
        filterSubscription={filterSubscription}
        setFilterSubscription={setFilterSubscription}
        filterPlanType={filterPlanType}
        setFilterPlanType={setFilterPlanType}
        filterSeatCategory={filterSeatCategory}
        setFilterSeatCategory={setFilterSeatCategory}
        rooms={rooms}
        totalStudents={students.length}
        filteredStudents={filtered}
        currentStudents={currentStudents}
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        handleItemsPerPageChange={(e) => setItemsPerPage(parseInt(e.target.value))}
        clearFilters={clearFilters}
      />

      <div className="panel table-panel student-directory" aria-busy={loading} style={{ opacity: refreshing && !loading ? 0.6 : 1, pointerEvents: refreshing && !loading ? 'none' : 'auto' }}>
        {loading ? <StudentsSkeleton /> : (
          <>
            <StudentDirectoryTable students={currentStudents} onView={setDetailsStudent} onEdit={onEdit} onDelete={onDelete} onPay={onPay} onNotify={onNotify} onStatusChange={handleStatusChange} statusSavingId={statusSavingId} />
            <div className="pagination-bar">
              <button disabled={currentPage===1} onClick={()=>setCurrentPage(1)}>First</button>
              <button disabled={currentPage===1} onClick={()=>setCurrentPage(p=>p-1)}>Prev</button>
              <span>Page {currentPage} of {totalPages || 1}</span>
              <button disabled={currentPage===totalPages || totalPages===0} onClick={()=>setCurrentPage(p=>p+1)}>Next</button>
              <button disabled={currentPage===totalPages || totalPages===0} onClick={()=>setCurrentPage(totalPages)}>Last</button>
            </div>
          </>
        )}
      </div>

      {detailsStudent && (
        <StudentDetailsModal
          student={detailsStudent}
          onClose={() => setDetailsStudent(null)}
          onEdit={onEdit}
          onPay={onPay}
          onNotify={onNotify}
        />
      )}
    </>
  );
}
